import { OwsAbacusContractClient } from "@abacus/ows-contract-client/fetch";
import { Logger } from "@abacus/common";

import { ResourceNotFoundError } from "@/resourceNotFoundError";
import { Runner } from "@/types";

import { TerminateContractRequest, TerminateContractTaskConfig } from "./types";
import {
  ContractLifecycle,
  ContractLifecycleStatus,
} from "@abacus/ows-contract-client";

export class TerminateContractTask implements Runner {
  contractClient: OwsAbacusContractClient;
  logger?: Logger;

  constructor({ contractClient, logger }: TerminateContractTaskConfig) {
    this.contractClient = contractClient;
    this.logger = logger;
  }

  async run({
    contractId,
    termination_effective,
    termination_notice_received,
  }: TerminateContractRequest): Promise<void> {
    // Get contract lifecycle
    let lifecycle = await this.getContractLifecycle(contractId);

    // Check if already terminated
    const isTerminated = this.isTerminated(lifecycle);

    this.logger?.info(
      [
        isTerminated ? `Already terminated:` : `Terminating:`,
        `Contract ID: ${lifecycle.contract_id},`,
        `Contract Lifecycle ID: ${lifecycle.contract_lifecycle_id},`,
        `Lifecycle Status: ${lifecycle.lifecycle_status},`,
        `Termination Effective: ${lifecycle.termination_effective}`,
      ].join(" "),
    );

    // Skip if already terminated
    if (isTerminated) {
      return;
    }

    // Terminate the contract
    await this.contractClient.terminateContract(
      {
        contractId,
      },
      {
        termination_effective,
        termination_notice_received,
      },
    );

    // Get contract lifecycle again
    lifecycle = await this.getContractLifecycle(contractId);

    this.logger?.info(
      [
        `Result:`,
        `Contract ID: ${lifecycle.contract_id},`,
        `Contract Lifecycle ID: ${lifecycle.contract_lifecycle_id},`,
        `Lifecycle Status: ${lifecycle.lifecycle_status},`,
        `Termination Effective: ${lifecycle.termination_effective}`,
      ].join(" "),
    );

    // Check if terminated successfully
    if (!this.isTerminated(lifecycle)) {
      throw new Error(`Contract ${contractId} could not be terminated`);
    }
  }

  private async getContractLifecycle(
    contractId: number,
  ): Promise<ContractLifecycle> {
    try {
      const response = await this.contractClient.getContractLifecycle({
        contractId,
      });
      return response.data;
    } catch (_) {
      throw new ResourceNotFoundError("contract-lifecycle", contractId);
    }
  }

  private isTerminated(lifecycle: ContractLifecycle): boolean {
    return lifecycle.lifecycle_status === ContractLifecycleStatus.terminated;
  }
}
